import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

// Utils & Enums
import { getTranslateByKey, isEmpty, isNullUndefined } from '@/main/services/utils';

// Redux
import { useUserReducer } from '@/main/store/user';
import { useCreateOrderContext } from '../../../context';

// Components
import HeadProductList from '@/main/components/shared/headProductList/headProductList';
import { FindMoreSuppliers } from '@/main/components/shared/card/findMoreSuppliers';
import LoadingComponent from '@/main/components/shared/loading/loadingComponent';
import CardShopping, {
  IPartsCard,
  ISupplierCard
} from '@/main/components/shared/card/cardShopping';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';

// Api's
import { quoteApi } from '@/main/api';

// Models
import { QuoteSelectSuppliersDataRequest } from '@/main/models/request';
import { QuoteSelectSuppliersDataResponse } from '@/main/models/response';
import { StatusQuoteSupplierItem } from '@/main/services/enum/StatusQuoteSupplierItem';

// Styles
import * as SP from '../styles';
import * as S from './styles';
import { useCartReducer } from '@/main/store/cart';
import { IMAGE_DEFAULT } from '@/main/services/constants';
import { RoutesConstants } from '@/main/routes/routesConstants';
import { useQueryParamsReducer } from '@/main/store/queryParams';
import CardPaintProject from '@/main/components/shared/card/cardPaintProject/cardPaintProject';
import { PartTypeEnum } from '@/main/services/enum/PartTypeEnum';
import { OrderStepsEnum } from '@/main/services/enum/OrderStepsEnum';

interface FiltersSelectSupplier {
  idsDamageArea: number[];
  idsPartType: string[];
}

interface IScreenPartsSupplierProps {
  selectedParts: string[];
  selectedDamageArea: string[];
  handleContinueShopping: () => void;
  displayPartsDetails: (
    guidSupplierInventoryGuid: string,
    guidQuoteSupplierItem: string
  ) => void;
  handleAddCart: (
    part: IPartsCard,
    suuplier: ISupplierCard,
    quantitity?: number
  ) => void;
  handleFinishOrder: () => void;
}

export interface IGridData {
  selectSupplierItems: QuoteSelectSuppliersDataResponse[];
  selectSupplierItemsInitial: QuoteSelectSuppliersDataResponse[];
  enableFindMoreSuppliers: boolean;
  foundSuppliersQuantity: number;
  minimalSuppliersQuantityForOrder: number;
  isLoading: boolean;
}

const INITIAL_GRID_STATE: IGridData = {
  selectSupplierItems: [],
  selectSupplierItemsInitial: [],
  enableFindMoreSuppliers: false,
  foundSuppliersQuantity: 0,
  minimalSuppliersQuantityForOrder: 0,
  isLoading: false
};

function ScreenPartsSupplier({
  displayPartsDetails,
  handleAddCart,
  handleFinishOrder,
  selectedParts,
  selectedDamageArea,
  handleContinueShopping
}: IScreenPartsSupplierProps) {
  const {
    displayModalCancel,
    handleSaveStepData,
    isCompareSupplier,
    partGuid,
    integration,
    isQuoteOpen,
    isQuoteManual,
    externalPartId,
    isSeeSuppliersAvailable
  } = useCreateOrderContext();
  const [gridData, setGridData] = useState<IGridData>(INITIAL_GRID_STATE);
  const [searchParams, _] = useSearchParams();
  const guidQuote = searchParams.get('guidQuote');
  const userReducer = useUserReducer();
  const { cartItems: cartReducer } = useCartReducer();
  const navigate = useNavigate();

  // Load by click in "Part" or "Compare supplier button"
  useEffect(() => {
    if ((partGuid || isCompareSupplier) && !isSeeSuppliersAvailable) {
      const request = getRequest(false);
      loadSelectSupplierItems(request, getAsideFiltersSelected());
    }
  }, [partGuid]);

  // Load by click in "See suppliers available button"
  useEffect(() => {
    if (isSeeSuppliersAvailable) {
      const request = getRequest(false);
      loadSelectSupplierItems(request, getAsideFiltersSelected());
    }
  }, [externalPartId]);

  // Load by click in "Aside filters"
  useEffect(() => {
    filterSelectSupplierItems(getAsideFiltersSelected());
  }, [selectedDamageArea, selectedParts]);

  // Loads
  function getRequest(findMoreSuppliers: boolean) {
    const request: QuoteSelectSuppliersDataRequest = {
      quoteGuid: String(guidQuote),
      partGuid,
      findMoreSuppliers,
      automaticQuote: false,
      isCompareSupplier
    };
    request.externalPartId = externalPartId != null ? externalPartId : null;
    return request;
  }

  function loadSelectSupplierItems(
    request: QuoteSelectSuppliersDataRequest,
    filters: FiltersSelectSupplier
  ) {
    setGridData({
      isLoading: true,
      selectSupplierItems: [],
      selectSupplierItemsInitial: [],
      enableFindMoreSuppliers: false,
      foundSuppliersQuantity: 0,
      minimalSuppliersQuantityForOrder: 0
    });
    quoteApi.getSelectSuppliers(request).then(response => {
      if (response.data.result) {
        const {
          enableFindMoreSuppliers,
          foundSuppliersQuantity,
          minimalSuppliersQuantityForOrder,
          selectSupplierItems
        } = response.data.result;
        let resultSelectSupplierItems: QuoteSelectSuppliersDataResponse[] = [];
        let resultSelectSupplierItemsInitial: QuoteSelectSuppliersDataResponse[] = [];

        if (selectSupplierItems && selectSupplierItems.length > 0) {
          resultSelectSupplierItems = selectSupplierItems;
          resultSelectSupplierItemsInitial = selectSupplierItems;

          // Filters
          if (resultSelectSupplierItems.length > 0) {
            // By DamageArea
            if (filters.idsDamageArea.length > 0) {
              resultSelectSupplierItems = filterByProp(
                resultSelectSupplierItems,
                filters.idsDamageArea,
                true
              );
            }

            // By PartType
            if (filters.idsPartType.length > 0) {
              resultSelectSupplierItems = filterByProp(
                resultSelectSupplierItems,
                filters.idsPartType,

                false
              );
            }
          }
        } else {
          if (request.findMoreSuppliers) {
            toast.error(
              getTranslateByKey(
                'information.notFoundSuppliersForThatPart',
                true
              )
            );
          }
        }

        setGridData({
          isLoading: false,
          selectSupplierItems: resultSelectSupplierItems,
          selectSupplierItemsInitial: resultSelectSupplierItemsInitial,
          enableFindMoreSuppliers,
          foundSuppliersQuantity,
          minimalSuppliersQuantityForOrder
        });

        if (request.findMoreSuppliers) {
          quoteApi.getSendSuppliersZeroInventoryEmail({
            quoteGuid: String(guidQuote)
          });
        }
      }
    });
  }

  function loadFindMoreSuppliers() {
    loadSelectSupplierItems(getRequest(true), getAsideFiltersSelected());
    handleSaveStepData(OrderStepsEnum.Summary, true, null)
  }

  // Filters
  function getAsideFiltersSelected() {
    const filters: FiltersSelectSupplier = {
      idsDamageArea:
        selectedDamageArea && selectedDamageArea.length > 0
          ? selectedDamageArea.map((value: string) => Number(value))
          : [],
      idsPartType:
        selectedParts && selectedParts.length > 0 ? selectedParts : []
    };

    return filters;
  }

  function filterSelectSupplierItems(filters: FiltersSelectSupplier) {
    let selectSupplierItemsFiltered: QuoteSelectSuppliersDataResponse[] =
      gridData.selectSupplierItemsInitial;

    // Filters
    if (selectSupplierItemsFiltered.length > 0) {
      // By DamageArea
      if (filters.idsDamageArea.length > 0) {
        selectSupplierItemsFiltered = filterByProp(
          selectSupplierItemsFiltered,
          filters.idsDamageArea,
          true
        );
      }

      // By PartType
      if (filters.idsPartType.length > 0) {
        selectSupplierItemsFiltered = filterByProp(
          selectSupplierItemsFiltered,
          filters.idsPartType,
          false
        );
      }

      setGridData({
        ...gridData,
        selectSupplierItems: selectSupplierItemsFiltered
      });
    }
  }

  function filterByProp(
    dataToFilter: QuoteSelectSuppliersDataResponse[],
    idsFilter: any[],
    isDamage: boolean
  ) {
    return dataToFilter.filter(
      (itemFilter: QuoteSelectSuppliersDataResponse) => {
        return idsFilter.includes(
          isDamage ? itemFilter.idDamageArea : itemFilter.partTypeGuid
        );
      }
    );
  }

  function onLoadCartQuantity(
    guidQuoteSupplierItem: string,
    guidSupplierInventoryWarehouse: string,
    idPartType: number,
    supplierGuid: string
  ) {
    const findSupplier = cartReducer.data.find(
      item => item.guid === supplierGuid
    );
    if (!findSupplier) return;
    const partCart = findSupplier.parts.find(
      item =>
        (item.guidSupplierInventoryWarehouse ===
          guidSupplierInventoryWarehouse ||
          item.guidQuoteSupplierItem === guidQuoteSupplierItem) &&
        item.idPartType === idPartType
    );

    if (!partCart) return;
    const { parts, ...restSupplier } = findSupplier;
    const formattedCart = {
      ...partCart,
      supplier: {
        ...restSupplier
      }
    };
    return formattedCart;
  }

  const { queryParams } = useQueryParamsReducer();
  function handleClickBack(e: React.KeyboardEvent<any>) {
    if (e.ctrlKey) {
      const saveFilters = !isNullUndefined(queryParams.data) ? queryParams.data[RoutesConstants.orderSearchOrder] : null;

      const params = new URLSearchParams();

      if (!isNullUndefined(saveFilters)) {
        Object.keys(saveFilters).forEach(key => {
          params.append(key, saveFilters[key]);
        });
      }

      const formattedParams = params.size > 0 ? `?${params}` : '';
      return window.open(`#${RoutesConstants.orderSearchOrder}${formattedParams}`);
    } else {
      return navigate(
        `${RoutesConstants.orderSearchOrder}`
      );
    }
  }

  // Validations
  const isBlockedCard = (item: QuoteSelectSuppliersDataResponse) =>
    (item.shipingIntegrationIsUnavailable && item.shipingIntegrationIsUnavailable) ||
    item.idQuoteSuppItemStatus === StatusQuoteSupplierItem.Pending ||
    item.notQuoted ||
    !item.price ||
    isNullUndefined(item.deliveryDays);
  const isPaintProject = gridData.selectSupplierItems.some(item => item.idPartType === PartTypeEnum.Paint);
  return (
    <>
      {gridData.isLoading ? (
        <LoadingComponent />
      ) : (
        <S.MainSupplierParts>
          <SP.BoxProductLayout>
            {gridData.isLoading ? (
              <LoadingComponent />
            ) : (
              <SP.Main
                className={
                  userReducer.userPreference.preferences.isViewCard
                    ? 'data-layout-grid'
                    : 'data-layout-column data-layout-column-supplier'
                }
                layoutGrid={userReducer.userPreference.preferences.isViewCard} id="id-createOrder-grid-supplier-card">
                <HeadProductList showSupplierParts={true} />
                {gridData.selectSupplierItems.map(
                  (item: QuoteSelectSuppliersDataResponse) =>
                    isPaintProject ?
                      <CardPaintProject
                        key={item.quoteItemGuid}
                        selectSupplier={item}
                        partInCart={onLoadCartQuantity(
                          item.guidQuoteSupplierItem,
                          item.supplierInventory_WarehouseGuid,
                          item.idPartType,
                          item.supplierGuid
                        )}
                      />
                      : (
                        <CardShopping
                          key={item.quoteItemGuid}
                          isShowSupplier
                          isBlocked={isBlockedCard(item)}
                          supplier={{
                            id: item.supplierGuid,
                            guid: item.supplierGuid,
                            name: item.supplier,
                            isRecommended: item.isRecomendedSupplier
                          }}
                          part={{
                            id: item.partCode,
                            guid: item.quoteItemGuid,
                            guidPart: partGuid,
                            title: item.partTitle,
                            image: item.image ? item.image : IMAGE_DEFAULT,
                            subTitle: item.partSubCategoryName,
                            partNumber: item.partCode,
                            partType: item.partType,
                            description: item.partDescription,
                            price: item.price,
                            deliveryTime: `${item.deliveryDays} ${getTranslateByKey(
                              'information.deliveryDays'
                            )}`,
                            shippingCost: item.shippingCost,
                            rating: item.score,
                            category: item.partCategoryName,
                            subCategory: item.partSubCategoryName,
                            guidSupplierInventoryWarehouse:
                              item.supplierInventory_WarehouseGuid,
                            idPartType: item.idPartType,
                            guidQuoteSupplierItem: item.guidQuoteSupplierItem,
                            partBrandDescription: item.partBrandDescription,
                            partBrandGuid: item.partBrandGuid,
                            isFavoriteSupplier: item.isFavoriteSupplier,
                            isRecomendedSupplier: item.isRecomendedSupplier,
                            stock: item.stock,
                            notQuoted: item.notQuoted,
                            deliveryBySupplier: item.deliveryBySupplier,
                            reason: item.reason,
                            reasonDescription: item.reasonDescription,
                            priceDiffPercent: item.priceDiffPercent,
                            shipingIntegrationIsUnavailable: item.shipingIntegrationIsUnavailable
                          }}
                          onClick={() => {
                            !isBlockedCard(item) &&
                              displayPartsDetails(
                                item.supplierInventoryGuid,
                                item.guidQuoteSupplierItem
                              );
                          }}
                          onClickCart={(part, supplier, quantitity) => {
                            if (!isBlockedCard(item)) {
                              handleAddCart(part, supplier, quantitity);
                            }
                          }}
                          style={{
                            minHeight: userReducer.userPreference.preferences
                              .isViewCard
                              ? '370px'
                              : '',
                            height: '100%'
                          }}
                          partInCart={onLoadCartQuantity(
                            item.guidQuoteSupplierItem,
                            item.supplierInventory_WarehouseGuid,
                            item.idPartType,
                            item.supplierGuid
                          )}
                        />
                      )
                )}
                {(gridData.enableFindMoreSuppliers && !isPaintProject) && (
                  <FindMoreSuppliers
                    onClick={loadFindMoreSuppliers}
                    style={{
                      minHeight: userReducer.userPreference.preferences
                        .isViewCard
                        ? '300px'
                        : 'auto',
                      height: '100%'
                    }}
                  />
                )}
              </SP.Main>
            )}
            <S.FooterSupplierParts>
              {!isCompareSupplier && (
                <>
                  <SecondaryButton
                    type="button"
                    onClick={(event) => handleClickBack(event)}
                    id="id-createOrder-button-back">
                    {getTranslateByKey('information.back')}
                  </SecondaryButton>
                  <SecondaryButton
                    style={{ marginLeft: '20px' }}
                    type="button"
                    onClick={() => {
                      displayModalCancel();
                    }}
                    id="id-createOrder-button-cancel"
                    disabled={
                      (integration && isQuoteOpen) ||
                      (integration && isQuoteManual)
                    }
                  >
                    {getTranslateByKey('information.cancel')}
                  </SecondaryButton>
                  <SecondaryButton
                    style={{ marginLeft: '20px' }}
                    type="button"
                    onClick={handleContinueShopping}
                    id="id-createOrder-button-continueShopping">
                    {getTranslateByKey('quotation.continueShopping')}
                  </SecondaryButton>
                </>
              )}
              <Button
                type="button"
                style={{ marginLeft: '20px' }}
                onClick={handleFinishOrder}
                id="id-createOrder-button-finishOrder"
                disabled={
                  (integration && isQuoteOpen) ||
                  (integration && isQuoteManual) ||
                  cartReducer.data.length === 0
                }
              >
                {getTranslateByKey('quotation.finishOrder')}
              </Button>
            </S.FooterSupplierParts>
          </SP.BoxProductLayout>
        </S.MainSupplierParts>
      )}
    </>
  );
}

export default ScreenPartsSupplier;
